import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ChemicalsService } from '../../../core/data-services/chemicals.service';
import type { Chemical } from '../../../core/data-models/chemical.model';
import type { ChemicalCategory } from '../../../core/data-models/chemical-category.model';
import type { SignalWord } from '../../../core/data-models/signal-word.model';
import type { ChemicalFilters } from '../../../core/request-models/chemical-filters.model';
import { ChemicalImportModalComponent } from '../chemical-import-modal/chemical-import-modal.component';

/**
 * Chemicals List Component
 *
 * Displays paginated list of ADN Recommended Chemicals with filtering,
 * search, and navigation to create/edit forms.
 */
@Component({
  selector: 'app-chemicals-list',
  standalone: true,
  imports: [CommonModule, FormsModule, ChemicalImportModalComponent],
  templateUrl: './chemicals-list.component.html',
  styleUrl: './chemicals-list.component.scss'
})
export class ChemicalsListComponent implements OnInit {
  chemicals: Chemical[] = [];
  loading = true;
  errorMessage = '';
  successMessage = '';

  // Pagination
  currentPage = 1;
  pageSize = 20;
  totalChemicals = 0;
  totalPages = 0;

  // Filters
  searchQuery = '';
  filterCategory: ChemicalCategory | 'all' = 'all';
  filterSignalWord: SignalWord | 'all' = 'all';
  filterFeatured: 'all' | 'featured' | 'non-featured' = 'all';
  filterSponsor: 'all' | 'has-sponsor' | 'no-sponsor' = 'all';

  // Category options
  categoryOptions: { value: ChemicalCategory | 'all'; label: string }[] = [
    { value: 'all', label: 'All Categories' },
    { value: 'herbicide', label: 'Herbicide' },
    { value: 'insecticide', label: 'Insecticide' },
    { value: 'fungicide', label: 'Fungicide' },
    { value: 'fertilizer', label: 'Fertilizer' },
    { value: 'adjuvant', label: 'Adjuvant' },
    { value: 'other', label: 'Other' }
  ];

  // Signal word options
  signalWordOptions: { value: SignalWord | 'all'; label: string }[] = [
    { value: 'all', label: 'All Signal Words' },
    { value: 'CAUTION', label: 'Caution' },
    { value: 'WARNING', label: 'Warning' },
    { value: 'DANGER', label: 'Danger' }
  ];

  // Featured options
  featuredOptions: { value: 'all' | 'featured' | 'non-featured'; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'featured', label: 'Featured Only' },
    { value: 'non-featured', label: 'Non-Featured' }
  ];

  // Sponsor options
  sponsorOptions: { value: 'all' | 'has-sponsor' | 'no-sponsor'; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'has-sponsor', label: 'Has Sponsor' },
    { value: 'no-sponsor', label: 'No Sponsor' }
  ];

  // Import modal state
  showImportModal = false;

  constructor(
    private router: Router,
    private chemicalsService: ChemicalsService
  ) {}

  ngOnInit(): void {
    this.loadChemicals();
  }

  /**
   * Load chemicals with current filters
   */
  loadChemicals(): void {
    this.loading = true;
    this.errorMessage = '';

    const filters: ChemicalFilters = {
      page: this.currentPage,
      limit: this.pageSize
    };

    if (this.searchQuery.trim()) {
      filters.query = this.searchQuery.trim();
    }

    if (this.filterCategory !== 'all') {
      filters.category = this.filterCategory;
    }

    if (this.filterSignalWord !== 'all') {
      filters.signalWord = this.filterSignalWord;
    }

    if (this.filterFeatured === 'featured') {
      filters.featured = true;
    } else if (this.filterFeatured === 'non-featured') {
      filters.featured = false;
    }

    if (this.filterSponsor === 'has-sponsor') {
      filters.sponsor = true;
    } else if (this.filterSponsor === 'no-sponsor') {
      filters.sponsor = false;
    }

    this.chemicalsService.listChemicals(filters).subscribe({
      next: (response) => {
        this.chemicals = response.items;
        this.totalChemicals = response.paging.totalItems;
        this.totalPages = response.paging.totalPages;
        this.loading = false;
      },
      error: (error) => {
        console.error('Failed to load chemicals:', error);
        this.errorMessage = 'Failed to load chemicals';
        this.loading = false;
      }
    });
  }

  /**
   * Handle search submission
   */
  onSearch(): void {
    this.currentPage = 1;
    this.loadChemicals();
  }

  /**
   * Handle filter change
   */
  onFilterChange(): void {
    this.currentPage = 1;
    this.loadChemicals();
  }

  /**
   * Clear all filters
   */
  clearFilters(): void {
    this.searchQuery = '';
    this.filterCategory = 'all';
    this.filterSignalWord = 'all';
    this.filterFeatured = 'all';
    this.filterSponsor = 'all';
    this.currentPage = 1;
    this.loadChemicals();
  }

  /**
   * Check if any filters are active
   */
  hasActiveFilters(): boolean {
    return (
      this.searchQuery !== '' ||
      this.filterCategory !== 'all' ||
      this.filterSignalWord !== 'all' ||
      this.filterFeatured !== 'all' ||
      this.filterSponsor !== 'all'
    );
  }

  /**
   * Navigate to page
   */
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadChemicals();
    }
  }

  /**
   * Get pagination range for display
   */
  get paginationRange(): number[] {
    const range: number[] = [];
    const maxButtons = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
    let end = Math.min(this.totalPages, start + maxButtons - 1);

    if (end - start < maxButtons - 1) {
      start = Math.max(1, end - maxButtons + 1);
    }

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  }

  /**
   * Navigate to chemical row (view/edit)
   */
  viewChemical(chemical: Chemical): void {
    this.router.navigate(['/chemicals', chemical.id, 'edit']);
  }

  /**
   * Navigate to edit page
   */
  editChemical(chemicalId: string): void {
    this.router.navigate(['/chemicals', chemicalId, 'edit']);
  }

  /**
   * Navigate to create page
   */
  createChemical(): void {
    this.router.navigate(['/chemicals/create']);
  }

  /**
   * Open import modal
   */
  openImportModal(): void {
    this.showImportModal = true;
  }

  /**
   * Close import modal
   */
  closeImportModal(): void {
    this.showImportModal = false;
  }

  /**
   * Handle successful import
   */
  onImportSuccess(result: { imported: number; skipped: number }): void {
    this.showImportModal = false;
    this.successMessage = `Successfully imported ${result.imported} chemical${result.imported === 1 ? '' : 's'}`;
    if (result.skipped > 0) {
      this.successMessage += `. ${result.skipped} row${result.skipped === 1 ? '' : 's'} skipped.`;
    }
    this.loadChemicals();
    this.clearSuccessMessage();
  }

  /**
   * Get category display label
   */
  getCategoryLabel(category: string | null | undefined): string {
    if (!category) return '-';
    const option = this.categoryOptions.find(opt => opt.value === category);
    return option?.label || category;
  }

  /**
   * Get signal word badge class
   */
  getSignalWordClass(signalWord: string | null | undefined): string {
    switch (signalWord?.toUpperCase()) {
      case 'DANGER':
        return 'bg-danger';
      case 'WARNING':
        return 'bg-warning text-dark';
      case 'CAUTION':
        return 'bg-info';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Clear success message after delay
   */
  private clearSuccessMessage(): void {
    setTimeout(() => {
      this.successMessage = '';
    }, 5000);
  }

  readonly Math = Math;
}
